import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "hooks";
import { selectProductionParks } from "store/selectors";
import { fetchAllProductionPark } from "store/appSlice";
import { Box, Button, Typography, Stack } from "@mui/material";
import Table from "components/Table";
import AddIcon from "@mui/icons-material/Add";
import { ProductionPark } from "api/models/ProductionPark";
import { ProductionParkType } from "api/models/enums/ProductionParkType";
import { ProtectedPaths } from "routes";
import { Link, useNavigate } from "react-router-dom";
import { formatNumberByThousands } from "helpers/numbers";
import { AttachFile } from "@mui/icons-material";
import LoadingButton from "@mui/lab/LoadingButton";
import { fetchExportProductionParks } from "api/resources/productionPark";
import { downloadXLSX } from "helpers/excelExport";

interface RowType {
  row: {
    original: ProductionPark;
  };
}

const ProductionParkTable = () => {
  const [currentPage, setCurrentPage] = useState(0);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const productionParks = useAppSelector(selectProductionParks);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [sortParam, setSortParam] = useState<string | undefined>(undefined);

  useEffect(() => {
    const fetchData = () => {
      setLoading(true);
      try {
        const allFilters = sortParam ? { sort: sortParam } : [];
        dispatch(fetchAllProductionPark(currentPage, 25, allFilters));
      } catch (error) {
        console.error("Failed to fetch production parks:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [currentPage, dispatch, setLoading, sortParam]);

  const handleSort = (field: string, direction: "asc" | "desc") => {
    setSortParam(`${field},${direction}`);
  };

  const handleExportProductionParks = () => {
    setExporting(true);

    fetchExportProductionParks()
      .then((response) => {
        downloadXLSX(response, `Production_parks.xlsx`);
      })
      .catch((error) => {
        console.error("Error exporting production parks", error);
      })
      .finally(() => {
        setExporting(false);
      });
  };

  const columns = [
    {
      accessorKey: "name",
      header: "Name",
      cell: (row: RowType) => (
        <span style={{ cursor: "pointer" }}>
          <Link
            to={`/${ProtectedPaths.EditProductionParks}/${row.row.original.id}`}
          >
            {row.row.original.name}
          </Link>
        </span>
      ),
    },
    {
      accessorKey: "code",
      header: "Code",
    },
    {
      accessorKey: "priceArea",
      header: "Price area",
    },
    {
      accessorKey: "deliveryType",
      header: "Delivery type",
    },
    {
      accessorKey: "type",
      header: "Type",
      cell: (row: RowType) => {
        return ProductionParkType[row.row.original.type];
      },
    },
    {
      accessorKey: "maxPowerOfInverters",
      header: "AC capacity (kW)",
      meta: { isNumeric: true },
      cell: (row: RowType) => {
        return formatNumberByThousands(
          Math.round(
            (row.row.original.maxPowerOfInverters + Number.EPSILON) * 100,
          ) / 100,
        );
      },
    },
    {
      accessorKey: "maxPowerOfSolarPanels",
      header: "DC capacity (kW)",
      meta: { isNumeric: true },
      cell: (row: RowType) => {
        return formatNumberByThousands(
          Math.round(
            (row.row.original.maxPowerOfSolarPanels + Number.EPSILON) * 100,
          ) / 100,
        );
      },
    },
    {
      accessorKey: "maxPowerOfWindGenerators",
      header: "Wind capacity (kW)",
      meta: { isNumeric: true },
      cell: (row: RowType) => {
        return formatNumberByThousands(
          Math.round(
            (row.row.original.maxPowerOfWindGenerators + Number.EPSILON) * 100,
          ) / 100,
        );
      },
    },
    {
      accessorKey: "maxPowerOfNetworks",
      header: "Network capacity (kW)",
      meta: { isNumeric: true },
      cell: (row: RowType) => {
        return formatNumberByThousands(
          Math.round(
            (row.row.original.maxPowerOfNetworks + Number.EPSILON) * 100,
          ) / 100,
        );
      },
    },
  ];

  return (
    <>
      <Box display="flex" justifyContent="space-between">
        <Typography variant="h5">Production parks</Typography>
        <Stack direction="row" alignItems="center" gap={1}>
          <LoadingButton
            loading={exporting}
            variant="outlined"
            size="small"
            startIcon={<AttachFile />}
            onClick={handleExportProductionParks}
          >
            Export production parks
          </LoadingButton>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => navigate(`/${ProtectedPaths.AddProductionParks}`)}
          >
            Add Production Park
          </Button>
        </Stack>
      </Box>
      <Table
        data={productionParks?.content}
        isFetching={loading}
        columns={columns}
        pageCount={productionParks?.totalPages}
        page={setCurrentPage}
        searchLabel="Search for metering point"
        onSort={handleSort}
      />
    </>
  );
};

export default ProductionParkTable;
